import { useRef, useState } from 'react';
import {
  EVIDENCE_ACCEPTED_EXTENSIONS,
  EVIDENCE_MAX_FILE_BYTES,
  evidenceDownloadUrl,
  formatFileSize,
  useDeleteEvidence,
  useUpdateEvidenceDescription,
  useUploadEvidence,
  type AuditEvidence,
} from '../../lib/api/queries';
import { ApiError } from '../../lib/api/client';
import { useDebouncedCallback } from '../../lib/useDebouncedCallback';

const CAPTION_DEBOUNCE_MS = 800;
const ACCEPT_ATTRIBUTE = EVIDENCE_ACCEPTED_EXTENSIONS.join(',');

/**
 * Evidence attachments for one criterion item: a drop zone plus the list of
 * what's already attached.
 *
 * Sits directly under the item's comment box, because the two are the same
 * act — the auditor says what they found and shows what they found it in.
 */
export function EvidenceStrip({
  tenantId,
  auditId,
  criterionItemId,
  evidence,
  editable,
}: {
  tenantId: string;
  auditId: string;
  criterionItemId: string;
  evidence: AuditEvidence[];
  editable: boolean;
}) {
  const upload = useUploadEvidence(tenantId, auditId);
  const remove = useDeleteEvidence(tenantId, auditId);
  const inputRef = useRef<HTMLInputElement>(null);

  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    // Sequential rather than parallel: the per-item cap is enforced
    // server-side per request, so firing five at once could put the count
    // over the limit with no way to say which one lost.
    for (const file of Array.from(files)) {
      const rejection = preValidate(file);
      if (rejection) {
        setError(rejection);
        continue;
      }

      try {
        setProgress(0);
        await upload.mutateAsync({
          criterionItemId,
          file,
          onProgress: setProgress,
        });
      } catch (err) {
        setError(describeUploadError(err, file.name));
      } finally {
        setProgress(null);
      }
    }

    // Clearing the input matters: without it, re-picking the same file
    // fires no change event and the upload silently doesn't happen.
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="mt-1">
      {evidence.length > 0 && (
        <ul className="flex flex-col gap-1.5 mb-2">
          {evidence.map(item => (
            <EvidenceRow
              key={item.id}
              tenantId={tenantId}
              auditId={auditId}
              evidence={item}
              editable={editable}
              onRemove={() => remove.mutate(item.id)}
            />
          ))}
        </ul>
      )}

      {editable && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault();
            setDragging(false);
            void handleFiles(e.dataTransfer.files);
          }}
          className={
            'flex items-center gap-2 px-3 py-2 rounded-md border border-dashed text-xs ' +
            'transition-colors ' +
            (dragging
              ? 'border-accent-alt-500 bg-accent-alt-50 text-accent-alt-700'
              : 'border-gray-300 text-gray-500 hover:border-gray-400')
          }
        >
          <PaperclipIcon />
          <span>
            Drop evidence here, or{' '}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={progress !== null}
              className="underline underline-offset-2 font-medium text-accent-alt-600
                         hover:text-accent-alt-700 disabled:opacity-50
                         disabled:cursor-not-allowed"
            >
              choose a file
            </button>
          </span>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTRIBUTE}
            onChange={e => void handleFiles(e.target.files)}
            className="sr-only"
          />
        </div>
      )}

      {progress !== null && (
        <div className="mt-1.5" role="status" aria-label="Upload progress">
          <div className="h-1 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full bg-accent-alt-500 transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-gray-500">Uploading… {progress}%</p>
        </div>
      )}

      {error && (
        <p className="mt-1.5 text-[11px] text-danger-500" role="alert">{error}</p>
      )}
    </div>
  );
}

function EvidenceRow({
  tenantId,
  auditId,
  evidence,
  editable,
  onRemove,
}: {
  tenantId: string;
  auditId: string;
  evidence: AuditEvidence;
  editable: boolean;
  onRemove: () => void;
}) {
  const updateCaption = useUpdateEvidenceDescription(tenantId, auditId);
  const [caption, setCaption] = useState(evidence.description ?? '');

  const saveCaption = useDebouncedCallback(
    (text: string) => updateCaption.mutate({
      evidenceId: evidence.id,
      description: text.length > 0 ? text : null,
    }),
    CAPTION_DEBOUNCE_MS,
  );

  const infected = evidence.scanStatus === 'Infected';

  return (
    <li className="flex flex-col gap-1 px-3 py-2 rounded-md border border-gray-200 bg-gray-50">
      <div className="flex items-start gap-2">
        <span className="shrink-0 mt-0.5 text-[10px] font-semibold tracking-wide
                         px-1.5 py-0.5 rounded bg-white border border-gray-300 text-gray-600">
          {evidence.referenceCode}
        </span>

        <div className="flex-1 min-w-0">
          {infected ? (
            <span className="text-xs font-medium text-danger-500">
              {evidence.fileName} — blocked by malware scan
            </span>
          ) : (
            <a
              href={evidenceDownloadUrl(auditId, evidence.id)}
              className="text-xs font-medium text-accent-alt-600 hover:text-accent-alt-700
                         underline underline-offset-2 break-all"
            >
              {evidence.fileName}
            </a>
          )}
          <span className="ml-2 text-[11px] text-gray-500">
            {formatFileSize(evidence.sizeBytes)}
          </span>
          {evidence.scanStatus === 'Pending' && (
            <span
              className="ml-2 text-[10px] text-gray-500"
              title="Awaiting a malware scan verdict"
            >
              scanning…
            </span>
          )}
        </div>

        {editable && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${evidence.fileName}`}
            className="shrink-0 text-[11px] text-gray-400 hover:text-danger-500
                       px-1 cursor-pointer"
          >
            Remove
          </button>
        )}
      </div>

      <input
        type="text"
        value={caption}
        onChange={e => { setCaption(e.target.value); saveCaption(e.target.value); }}
        disabled={!editable}
        placeholder="Caption for the report (optional)"
        className="w-full px-2 py-1 border border-gray-200 rounded text-[11px] text-gray-700
                   bg-white focus:outline-none focus:ring-2 focus:ring-accent-alt-500
                   focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
      />
    </li>
  );
}

/**
 * Cheap client-side checks so an obviously-doomed upload never leaves the
 * browser. The server repeats all of this — including a leading-bytes check
 * this can't do — so anything that slips past here is still refused.
 */
function preValidate(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';

  if (!(EVIDENCE_ACCEPTED_EXTENSIONS as readonly string[]).includes(extension)) {
    return `${file.name} isn't a file type that can be attached as evidence.`;
  }
  if (file.size === 0) {
    return `${file.name} is empty.`;
  }
  if (file.size > EVIDENCE_MAX_FILE_BYTES) {
    return `${file.name} is ${formatFileSize(file.size)} — the limit is ` +
           `${formatFileSize(EVIDENCE_MAX_FILE_BYTES)}.`;
  }
  return null;
}

function describeUploadError(err: unknown, fileName: string): string {
  // The server's 400 body is a plain-text sentence written for the auditor
  // (see DescribeUploadFailure), so pass it through rather than replacing
  // it with something vaguer.
  if (err instanceof ApiError) {
    if (typeof err.body === 'string' && err.body.length > 0) return err.body;
    if (err.status === 0) return `${fileName} didn't upload — check your connection.`;
    if (err.status === 413) return `${fileName} is too large to upload.`;
  }
  return `${fileName} couldn't be attached.`;
}

function PaperclipIcon() {
  return (
    <svg
      className="w-3.5 h-3.5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66
               l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}
